import React, { useEffect, useState } from 'react';

import { useStyles } from './Themable.hooks';

import { useAppDispatch } from '../../../../hooks';

import TranslationHelper from '../../../../helpers/TranslationHelper';

import DialogTitle from '../../../dialogs/DialogTitle';
import LoadingSpinner from '../../../loadingSpinner/LoadingSpinner';
import useToggle from '../../../../hooks/useToggle';
import { Dialog, DialogContent } from '@mui/material';
import useAsyncThunkWithSnackbar from '../../../../hooks/useAsyncThunkWithSnackbar';
import {
    useSelectedTicketAvailableStatuses,
    useTicketHistory,
    useTicketHistoryStatus,
    useTicketsSourceSetModel,
} from '../../../../features/Tickets/state/index.hooks';
import SourceSetGrid from '../../../SourceSetGrid';
import { Paper } from '@material-ui/core';
import { fetchTicketHistory } from '../../../../features/Tickets/state';
import { hideGlobalDialog } from '../../../../state/ui/dialog';
import { GridApi } from 'ag-grid-community';
import { useUsers } from '../../../../state/app/collections/index.hooks';

interface IOwnProps {
    ticketId: string;
}

const TicketHistoryGridDialog = ({ ticketId }: IOwnProps) => {
    const classes = useStyles();

    const [gridApi, setGridApi] = useState<GridApi | null>(null);
    const dispatch = useAppDispatch();
    const { isOpen, handleToggle } = useToggle(true);
    const ticketHistoryStatus = useTicketHistoryStatus();
    const ticketHistory = useTicketHistory();
    const ticketsSourceSetModel = useTicketsSourceSetModel();
    const handleAsyncRequest = useAsyncThunkWithSnackbar();
    const users = useUsers();
    const statuses = useSelectedTicketAvailableStatuses();

    useEffect(() => {
        if (ticketId) {
            handleAsyncRequest({
                asyncAction: fetchTicketHistory(ticketId),
            });
        }
    }, []);

    useEffect(() => {
        if (gridApi && statuses) {
            const status = gridApi.getColumnDef('status');
            if (status) {
                status.valueGetter = (params) => {
                    const statusId = params.data.status;
                    return statuses.find((status) => status.name === statusId)
                        ?.label;
                };
            }
            ['assignedUser', 'author'].forEach((key) => {
                const column = gridApi.getColumnDef(key);
                if (column) {
                    column.valueGetter = (params) => {
                        // @ts-ignore
                        const userId = params.data[key] as number;
                        return users?.entities?.[userId]?.login || '';
                    };
                }
            });
            gridApi.refreshCells({
                columns: ['status', 'assignedUser', 'author'],
                force: true,
            });
        }
    }, [gridApi, statuses]);

    const handleSetApiGrid = (gridApi: GridApi) => {
        setGridApi(gridApi);
    };

    const handleCloseDialog = (event?: object, reason?: string) => {
        if (reason === 'backdropClick') {
            return;
        }
        dispatch(hideGlobalDialog());
        handleToggle();
    };

    return (
        <Dialog
            open={isOpen}
            onClose={handleCloseDialog}
            maxWidth={'lg'}
            classes={{ paper: classes.paper }}
        >
            <DialogTitle
                title={`${TranslationHelper.translate('Ticket History')}`}
                close={handleCloseDialog}
                loading={ticketHistoryStatus === 'loading'}
            ></DialogTitle>
            <DialogContent>
                <div className={classes.wrapper}>
                    {ticketHistoryStatus === 'loading' ? (
                        <LoadingSpinner
                            size="50"
                            top="0"
                            left="0"
                            right="0"
                            bottom="0"
                        />
                    ) : (
                        <Paper className={classes.gridPane}>
                            <div className={classes.gridWrapper}>
                                {ticketHistory && (
                                    <SourceSetGrid
                                        setGridApi={handleSetApiGrid}
                                        sourceSet={ticketHistory}
                                        sourceSetModel={ticketsSourceSetModel}
                                        selectedSourceSetElementId={ticketId}
                                    />
                                )}
                            </div>
                        </Paper>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default TicketHistoryGridDialog;
